// ⌘K — the rows an installed app opts into the workspace search
// (CMDK-FINDS-APP-RECORDS-1): fleet nodes, incidents, networks.
//
// An app marks a collection searchable in its own manifest
// (`collections[].search: { title, subtitle }`); the server answers the
// member search over those collections only (surface 'app-records': the
// caller's records grant and field rules apply, matched on the declared
// fields only), and each result is an entity ref with its title, its
// subtitle and the app it belongs to. This builder turns those results into
// dropdown rows:
//
//   - one GROUP per app, labelled with the app's own name (tenant content —
//     polyglot passes it through unchanged), right after People;
//   - a row OPENS on the module page that owns its collection, through the
//     record-route registry (`entityRefHref`) — a result nothing in this
//     workspace can open is dropped, never shown as a dead row.
//
// A plain builder, not an `el.call` function — imported directly by
// globalSearch.js, like searchAppResults.js and searchCommandResults.js.

export const APP_RECORDS_SURFACE = 'app-records'
export const APP_RECORDS_CATEGORY_PREFIX = 'records:'
// Right after People (0), ahead of Tasks (1): a name typed into ⌘K in a fleet
// workspace is most often a node.
export const APP_RECORDS_ORDER = 0.5

export const isAppRecordsCategory = (cat) =>
  typeof cat === 'string' && cat.startsWith(APP_RECORDS_CATEGORY_PREFIX)

const _safe = (fn, ...args) => {
  try {
    return fn ? fn(...args) : null
  } catch (_) {
    return null
  }
}

// ── ⌘K filter chips and the hand-off (CMDK-05 / CMDK-08) ───────────────────
// An app's opt-in may name CHIP fields (`facets`) and the app page that takes
// the matched rows (`open`). The server answers an app-records search with the
// chip values and their counts (`facets`) and that page (`handoffs`); a chip
// rides the query as a `field:value` token. These builders turn that into the
// palette's three extra groups — the active chips, the values to filter by,
// and what to do with the result — as plain rows with an `_searchAction` the
// shell runs (globalSearch.js runSearchAction). Every visible word is either
// tenant content (an app's own labels, values and names) or a bundle KEY the
// row renders through polyglot (`_titleKey` / `_metaKey` + their params).

export const SEARCH_CHIPS_CATEGORY = 'search-chips'
export const SEARCH_FILTERS_CATEGORY = 'search-filters'
export const SEARCH_ACTIONS_CATEGORY = 'search-actions'
const FILTER_ROWS_IDLE = 6
const FILTER_ROWS_FOR_TERM = 4
const CHIP_FIELD = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/

const _chipOk = (c) =>
  !!c &&
  typeof c.field === 'string' &&
  CHIP_FIELD.test(c.field) &&
  typeof c.value === 'string' &&
  !!c.value.trim() &&
  c.value.length <= 120 &&
  !c.value.includes('"')

export const searchChipKey = (c) => (c ? `${c.field}:${c.value}` : '')

// The chips as the query tokens the server reads (`field:value`, quoted when
// the value holds a space). A value the server cannot carry is left out.
export function serializeSearchChips(chips) {
  return (Array.isArray(chips) ? chips : [])
    .filter(_chipOk)
    .map((c) => (/\s/.test(c.value) ? `${c.field}:"${c.value}"` : `${c.field}:${c.value}`))
    .join(' ')
}

// "Environment · Mainnet" — the chip as a member reads it.
const _recordChipText = (c) => `${c.fieldLabel || c.field} · ${c.label || c.value}`

// The active chips, each a row that removes it.
export function activeChipRows(chips) {
  return (Array.isArray(chips) ? chips : []).filter(_chipOk).map((c) => ({
    _category: SEARCH_CHIPS_CATEGORY,
    _id: 'chip:' + searchChipKey(c),
    _title: _recordChipText(c),
    _metaKey: 'workspace.search.chip.remove',
    _icon: 'close',
    _searchAction: { type: 'chip-remove', key: searchChipKey(c) }
  }))
}

// Which lists a value spans, as the member reads them — "Bills 1 · Invoices 1"
// — or '' when it is one list, or when the parts cannot be told apart. A part
// is named by its list's own label (the app's search opt-in `label`); without
// one, by its app's name, which tells lists apart only across apps — two
// unnamed lists of one app get no hint (their total stays right).
export function facetSpanHint(parts) {
  const list = Array.isArray(parts) ? parts.filter((p) => p && Number(p.count) > 0) : []
  if (list.length < 2) return ''
  const apps = new Set(list.map((p) => p.appId || p.appName || ''))
  const named = list.map((p) => ({
    name: p.listLabel || (apps.size > 1 ? p.appName : '') || '',
    count: Number(p.count) || 0
  }))
  if (named.some((p) => !p.name)) return ''
  if (new Set(named.map((p) => p.name)).size < named.length) return ''
  return named.map((p) => `${p.name} ${p.count}`).join(' · ')
}

// The values to filter by: one row per field + value (counts summed across
// the collections that declare the field), never a value already chipped.
// A value that spans two or more lists carries `_hint` (facetSpanHint) — the
// row's tooltip and accessible description; a one-list value carries none.
// With a term, only the values whose label, raw value or field name contains
// it (a typed "Pinecrest" offers "Customer · Pinecrest Custody"); with none,
// the most common values first.
export function facetFilterRows(facets, { term = '', chips = [], limit } = {}) {
  const active = new Set((Array.isArray(chips) ? chips : []).map(searchChipKey))
  const merged = new Map()
  for (const f of Array.isArray(facets) ? facets : []) {
    if (!f || typeof f.field !== 'string' || !Array.isArray(f.values)) continue
    for (const v of f.values) {
      if (!v || typeof v.value !== 'string') continue
      const chip = {
        field: f.field,
        value: v.value,
        label: typeof v.label === 'string' && v.label ? v.label : v.value,
        fieldLabel: typeof f.label === 'string' && f.label ? f.label : f.field
      }
      if (!_chipOk(chip)) continue
      const key = searchChipKey(chip)
      if (active.has(key)) continue
      const prev = merged.get(key)
      const count = Number(v.count) || 0
      const part = {
        collection: f.collection,
        listLabel: typeof f.listLabel === 'string' ? f.listLabel : '',
        appId: (f.app && f.app.id) || '',
        appName: (f.app && f.app.name) || '',
        count
      }
      merged.set(key, {
        chip,
        count: (prev ? prev.count : 0) + count,
        parts: [...(prev ? prev.parts : []), part]
      })
    }
  }
  const t = String(term || '')
    .trim()
    .toLowerCase()
  let rows = [...merged.values()]
  if (t) {
    rows = rows.filter(({ chip }) =>
      [chip.label, chip.value, chip.fieldLabel].some((s) => String(s).toLowerCase().includes(t))
    )
  }
  rows.sort((a, b) => b.count - a.count || _recordChipText(a.chip).localeCompare(_recordChipText(b.chip)))
  const cap = limit || (t ? FILTER_ROWS_FOR_TERM : FILTER_ROWS_IDLE)
  return rows.slice(0, cap).map(({ chip, count, parts }) => {
    const hint = facetSpanHint(parts)
    return {
      _category: SEARCH_FILTERS_CATEGORY,
      _id: 'filter:' + searchChipKey(chip),
      _title: _recordChipText(chip),
      _meta: String(count),
      ...(hint ? { _hint: hint } : {}),
      _icon: 'filter',
      _searchAction: { type: 'chip-add', chip }
    }
  })
}

// How many rows the search matched — the server's per-collection `totals`
// (every collection read whole: the same rows the chip counts come from),
// never the page of rows it returned. Live on dev (2026-10-07, Finances chip
// "Cost category · Servers"): the action row said "5 found" beside chip
// counts of 15, because only a hand-off carried a total and the page length
// stood in for the rest. → { count, capped } (`capped`: the count is a floor —
// a collection matched more rows than the server reads, or an older server
// sent no totals and the page was full).
export function searchMatchTotal({ totals, handoffs, results, limit } = {}) {
  const list = Array.isArray(totals) ? totals.filter(Boolean) : []
  if (list.length) {
    return {
      count: list.reduce((n, t) => n + (Number(t.total) || 0), 0),
      capped: list.some((t) => t.capped === true)
    }
  }
  const fromHandoffs = (Array.isArray(handoffs) ? handoffs : []).reduce(
    (n, h) => n + (Number(h?.total) || 0),
    0
  )
  if (fromHandoffs) return { count: fromHandoffs, capped: false }
  const page = Array.isArray(results) ? results.length : 0
  return { count: page, capped: !!limit && page >= limit }
}

const _recordCountText = (n, capped) => (capped ? `${n}+` : n)

// What to do with the chipped result: each app page that takes the matched
// rows (the app's own label, or "Open in <app>"), the ask hand-off, and the
// clear. Only when a chip is active — a plain search stays a plain list.
// `total` / `totalCapped` are searchMatchTotal's; `totals` the server's
// per-collection list (a hand-off's count is a floor only when its
// collection's total is).
export function searchActionRows({
  chips = [],
  handoffs = [],
  total,
  totalCapped = false,
  totals = []
} = {}) {
  if (!(Array.isArray(chips) ? chips : []).some(_chipOk)) return []
  const rows = []
  for (const h of Array.isArray(handoffs) ? handoffs : []) {
    if (!h || typeof h.route !== 'string' || typeof h.param !== 'string') continue
    const values = Array.isArray(h.values) ? h.values.filter((v) => typeof v === 'string' && v) : []
    if (!values.length) continue
    const appName = (h.app && h.app.name) || ''
    rows.push({
      _category: SEARCH_ACTIONS_CATEGORY,
      _id: 'handoff:' + (h.collection || h.route),
      ...(h.label
        ? { _title: h.label }
        : { _titleKey: 'workspace.search.action.openIn', _titleParams: { app: appName } }),
      _metaKey: 'workspace.search.action.rows',
      _metaParams: {
        count: _recordCountText(
          Number(h.total) || values.length,
          (Array.isArray(totals) ? totals : []).some(
            (t) => t && t.collection === h.collection && t.capped === true
          )
        )
      },
      _icon: 'arrowRight',
      _searchAction: { type: 'handoff', route: h.route, param: h.param, values }
    })
  }
  const n = Number(total) || 0
  if (n) {
    rows.push({
      _category: SEARCH_ACTIONS_CATEGORY,
      _id: 'ask',
      _titleKey: 'workspace.search.action.ask',
      _metaKey: 'workspace.search.action.rows',
      _metaParams: { count: _recordCountText(n, totalCapped) },
      _icon: 'chat',
      _searchAction: { type: 'ask' }
    })
  }
  rows.push({
    _category: SEARCH_ACTIONS_CATEGORY,
    _id: 'clear',
    _titleKey: 'workspace.search.action.clear',
    _icon: 'dismiss',
    _searchAction: { type: 'chips-clear' }
  })
  return rows
}

// The hand-off's in-shell href: the app's route with the matched rows'
// values in its own parameter (`/validators/intel?show=a,b`).
export function searchHandoffHref(action) {
  if (!action || typeof action.route !== 'string' || typeof action.param !== 'string') return ''
  if (action.route[0] !== '/' || action.route[1] === '/' || /[?#\s]/.test(action.route)) return ''
  const values = (Array.isArray(action.values) ? action.values : []).filter(
    (v) => typeof v === 'string' && v
  )
  if (!values.length || !CHIP_FIELD.test(action.param.replace(/-/g, '_'))) return ''
  return `${action.route}?${encodeURIComponent(action.param)}=${values.map(encodeURIComponent).join(',')}`
}

export function appRecordSearchHits(results, { hrefFor, iconFor, limit = 5 } = {}) {
  const out = []
  for (const r of Array.isArray(results) ? results : []) {
    if (out.length >= limit) break
    if (!r || typeof r.ref !== 'string' || r.surface !== APP_RECORDS_SURFACE) continue
    const href = _safe(hrefFor, r.ref)
    if (typeof href !== 'string' || !href) continue
    const appId = (r.app && typeof r.app.id === 'string' && r.app.id) || r.collection || 'records'
    const icon = _safe(iconFor, r.ref)
    out.push({
      _category: APP_RECORDS_CATEGORY_PREFIX + appId,
      _groupLabel: (r.app && typeof r.app.name === 'string' && r.app.name) || r.collection || '',
      ...(typeof icon === 'string' && icon ? { _groupIcon: icon } : {}),
      _id: r.ref,
      _title: (typeof r.title === 'string' && r.title) || r.ref,
      _subtitle: typeof r.snippet === 'string' ? r.snippet : '',
      _href: href
    })
  }
  return out
}
